import { app, authentication } from '@microsoft/teams-js';
import axios from 'axios';
import config from '../../utils/config';
import { ClientsApi, OptionsApi, UserApi } from '../api';

/**
 * Get an access token for the backend API using Teams SSO.
 * First attempts silent authentication, then falls back to interactive if needed.
 */
async function getAccessToken(): Promise<string> {
    try {
        // Teams SDK SSO - first attempt silent authentication
        const token = await authentication.getAuthToken({
            resources: [config.apiScope],
            silent: true,
        });
        return token;
    } catch (silentError) {
        console.warn('Silent auth failed, retrying with user interaction:', silentError);
        try {
            // Retry with interactive consent if silent authentication fails
            const token = await authentication.getAuthToken({
                resources: [config.apiScope],
                silent: false,
            });
            return token;
        } catch (interactiveError) {
            console.error('Failed to get auth token:', interactiveError);
            throw new Error(
                'Failed to acquire authentication token. This may be a temporary issue; please try again. If the problem persists, sign out and sign in again.'
            );
        }
    }
}

/**
 * Get the current user's email from Teams context.
 * Used as a fallback for backend user identification.
 */
async function getUserEmail(): Promise<string | undefined> {
    try {
        const context = await app.getContext();
        return context.user?.userPrincipalName;
    } catch {
        return undefined;
    }
}

export const useClientOnboardingApi = () => {
    const axiosInstance = axios.create({
        baseURL: config.backendUrl,
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
    });

    // Add auth token and user info to each request
    axiosInstance.interceptors.request.use(async (request) => {
        if (config.disableAuth) {
            return request;
        }

        const [token, userEmail] = await Promise.all([getAccessToken(), getUserEmail()]);

        request.headers['Authorization'] = `Bearer ${token}`;

        // Include user email for backend audit logging (fallback if not in token claims)
        if (userEmail) {
            request.headers['x-user-email'] = userEmail;
        }

        return request;
    });

    return {
        ClientsApi: new ClientsApi(undefined, config.backendUrl, axiosInstance),
        UserApi: new UserApi(undefined, config.backendUrl, axiosInstance),
        OptionsApi: new OptionsApi(undefined, config.backendUrl, axiosInstance),
    };
};
